"use client";

import {
  IconArrowsClockwise,
  IconMegaphone,
  IconMinusCircle,
  IconPlusCircle,
  IconUsersThree,
  IconWrench,
} from "@decibeltrade/react-icons";
import { focusRing } from "@decibeltrade/web-core/recipes";
import { useTranslations } from "next-intl";
import { css, cva, cx } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { Clickable } from "@/components/clickable";
import { AnnouncementCategory } from "@/contexts/queries/announcements";

import { useCategory } from "../use-category";

interface CategorySidebarProps {
  selectedCategory: AnnouncementCategory | null;
}

interface CategoryItem {
  category: AnnouncementCategory | null;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
}

const categories: CategoryItem[] = [
  { category: null, labelKey: "allAnnouncements", icon: IconMegaphone },
  { category: "listings", labelKey: "newMarkets", icon: IconPlusCircle },
  { category: "delistings", labelKey: "delistedMarkets", icon: IconMinusCircle },
  { category: "parameter-updates", labelKey: "parameterUpdates", icon: IconWrench },
  { category: "product-updates", labelKey: "productUpdates", icon: IconArrowsClockwise },
  { category: "community", labelKey: "community", icon: IconUsersThree },
];

export function CategorySidebar({ selectedCategory }: CategorySidebarProps) {
  const t = useTranslations("announcements");
  const { setCategory } = useCategory();

  return (
    <nav className={stack({ gap: "8" })}>
      {categories.map((item) => {
        const isActive =
          item.category === selectedCategory ||
          (item.category === null && selectedCategory === null);
        const Icon = item.icon;

        return (
          <Clickable
            key={item.labelKey}
            onClick={() => setCategory(item.category)}
            className={cx(navItemStyles({ active: isActive }), focusRing({ rounded: "350" }))}
          >
            <Icon
              className={css({
                w: "20",
                h: "20",
                color: isActive ? "text.primary" : "text.tertiary",
              })}
            />
            <span>{t(`categories.${item.labelKey}`)}</span>
          </Clickable>
        );
      })}
    </nav>
  );
}

const navItemStyles = cva({
  base: {
    display: "flex",
    alignItems: "center",
    gap: "8",
    px: "12",
    py: "8",
    rounded: "300",
    transitionPreset: "colors",
    _hover: {
      bg: "background.secondary",
    },
  },
  variants: {
    active: {
      true: {
        bg: "background.primary",
        textStyle: "heading.3xs",
        color: "text.primary",
      },
      false: {
        bg: "transparent",
        textStyle: "body.lg",
        color: "text.tertiary",
      },
    },
  },
  defaultVariants: {
    active: false,
  },
});
